import { fromCents, toCents } from '@payer-admin-ui/components-v2/src/utils/currency';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { bankFilePaymentsAPI, MappingRequest } from 'redux/bankFilePaymentsAPI';
import { invoiceAggregationAPI } from 'redux/invoiceAggregationAPI';
import { invoiceAPI } from 'redux/invoiceAPI';
import { MappingTargetType } from 'utils/constants';
import { InvoiceAggregationsFilters } from 'views/Pages/invoiceAggregations/invoiceAggregationsGlobalState';
import { type InvoiceFilters } from 'views/Pages/invoices/invoicesGlobalState';
import { showSnackbar } from 'redux/snackbar';
import i18n from 'i18next';
import {
  type AddMappingModalState,
  type DraftInvoiceMapping,
  type InvoiceSearchResultItem,
  type StatementSearchResultItem,
} from './AddMappingModalStateConstants';

const toMappingRequest = (draft: DraftInvoiceMapping): MappingRequest => {
  return {
    amount: toCents(draft.amount),
    invoiceId: draft.number ? Number(draft.number) : undefined,
    mappingType: draft.mappingType,
  };
};

export const searchInvoices = createAsyncThunk(
  'bankFilePayments/actions/addMappingModal/searchInvoices',
  async ({ query }: { query: string }, { getState }) => {
    if (query.trim() === '') return [] as InvoiceSearchResultItem[];
    const state: any = getState();
    const modalState: AddMappingModalState = state.bankFilePaymentActionAddMappingModal;
    const applied = modalState.invoice.filters.appliedFilters;
    const filters: InvoiceFilters = { ...applied, search: query };
    const invoiceResponse = await invoiceAPI.fetchPage({ page: 0, pageSize: 50, filters, sort: [] });
    const invoices = invoiceResponse.contents;
    const invoiceItems: InvoiceSearchResultItem[] = invoices.map(inv => {
      const company = inv.invoiceCustomer?.address?.companyName || '';
      const firstName = inv.invoiceCustomer?.address?.firstName || '';
      const lastName = inv.invoiceCustomer?.address?.lastName || '';
      const customerName = company || `${firstName} ${lastName}`.trim();
      return {
        type: inv.type as unknown as MappingTargetType,
        number: String(inv.invoiceNumber),
        customerName,
        currencyCode: String(inv.currencyCode),
        balance: fromCents(inv.balance),
        originalAmount: fromCents(inv.amountToPay),
        paymentStatus: inv.paymentStatus,
        invoiceDate: inv.invoiceDate ? String(inv.invoiceDate) : undefined,
        dueDate: inv.dueDate ? String(inv.dueDate) : undefined,
        feeBalance: fromCents(inv.feeBalance),
        reference: inv.externalReference?.reference ? inv.externalReference.reference : inv.referenceNumber,
        invoice: inv,
      };
    });
    return invoiceItems;
  },
);

export const searchStatements = createAsyncThunk(
  'bankFilePayments/actions/addMappingModal/searchStatements',
  async ({ query }: { query: string }, { getState }) => {
    if (query.trim() === '') return [] as StatementSearchResultItem[];
    const state: any = getState();
    const modalState: AddMappingModalState = state.bankFilePaymentActionAddMappingModal;
    const applied = modalState.statement.filters.appliedFilters;
    const iaFilters: InvoiceAggregationsFilters = {
      ...applied,
      search: query,
    };
    const iaResponse = await invoiceAggregationAPI.fetchPage({
      page: 0,
      pageSize: 50,
      appliedFilters: iaFilters,
      sort: [],
    });
    const ias = iaResponse.contents;
    const iaItems: StatementSearchResultItem[] = ias.map(ia => {
      return {
        number: String(ia.invoiceAggregationNumber),
        currencyCode: ia.currencyCode,
        dueDate: ia.dueDate,
        customerCompany: ia.invoiceCustomerCompany,
        numberOfInvoices: ia.countInvoices,
        originalAmount: fromCents(ia.originalAmountToPay),
        balance: fromCents(ia.balance),
        ocrNumber: ia.ocrNumber,
        statement: ia,
      };
    });
    return iaItems;
  },
);

export const applyInvoiceDraftMappings = createAsyncThunk(
  'bankFilePayments/actions/addMappingModal/applyInvoiceDraftMappings',
  async ({ bankFilePaymentId }: { bankFilePaymentId: number }, { getState, dispatch }) => {
    const state: any = getState();
    const modalState: AddMappingModalState = state.bankFilePaymentActionAddMappingModal;
    const drafts = Object.values(modalState?.invoice.draftMappings || {});
    const mappings = drafts.filter(d => d.amount != null).map(toMappingRequest);
    await bankFilePaymentsAPI.bankFilePayments.manualMap({
      bankFilePaymentId: String(bankFilePaymentId),
      payinMappings: mappings,
      allowPartialMapping: true,
    });
    const type = i18n.t('bankFilePayments.fields.invoice').toLowerCase();
    dispatch(
      showSnackbar(
        i18n.t('bankFilePayments.merchantMappingModal.mappingsApplied', {
          bankFilePaymentId,
          count: mappings.length,
          type,
        }),
        { severity: 'success' },
      ),
    );
  },
);

export const applyStatementDraftMappings = createAsyncThunk(
  'bankFilePayments/actions/addMappingModal/applyStatementDraftMappings',
  async ({ bankFilePaymentId }: { bankFilePaymentId: number }, { getState, dispatch }) => {
    const state: any = getState();
    const modalState: AddMappingModalState = state.bankFilePaymentActionAddMappingModal;
    const drafts = Object.values(modalState?.statement.draftMappings || {});
    const statementIdsAndAmounts = drafts
      .filter(d => d.number && d.amount != null)
      .map(d => ({ invoiceAggregationId: d.number as string, amount: String(toCents(d.amount)) }));
    await bankFilePaymentsAPI.bankFilePayments.mapToIas({
      bankFilePaymentId,
      debitInvoiceAggregationIdsAndAmounts: statementIdsAndAmounts,
    });
    const type = i18n.t('bankFilePayments.fields.statement').toLowerCase();
    dispatch(
      showSnackbar(
        i18n.t('bankFilePayments.merchantMappingModal.mappingsApplied', {
          bankFilePaymentId,
          count: statementIdsAndAmounts.length,
          type,
        }),
        { severity: 'success' },
      ),
    );
  },
);
